<template>
  <div>
    <custom-tabs
      v-if="loadedOnce"
      v-model="activeTab"
      @input="onTabChange"
      class="category-tabs"
      :animated="false"
    >
      <custom-tab-item
        v-for="section of sections"
        :key="section.name"
        :label="$_.get(messages, `news.sections.${section.name}`)"
        :icon="section.icon"
        :visible="getSectionTabVisibility(section)"
        :visible-label="getSectionTabLabelVisibility(section)"
      />
    </custom-tabs>
    <div
      ref="scrollableContainer"
      v-infinite-scroll="debouncedFetch"
      :infinite-scroll-immediate-check="() => false"
      infinite-scroll-disabled="infiniteScrollDisabled"
      infinite-scroll-distance="1000"
    >
      <post
        v-for="article in articles"
        :key="article.id"
        :value="article"
        @open="onPostOpen"
        @tag-click="onTagClick"
        :active-tags="tags"
        :current-section="sectionName"
        v-observe-visibility="{
          once: true,
          callback: (isVisible, entry) =>
            onPostVisibilityChange(isVisible, entry, article),
          intersection: {
            threshold: 0.1,
          },
        }"
      />
    </div>
    <template v-if="this.reachedEndOfFeed && this.articles.length == 0">
      <p class="has-text-centered" style="margin-top: 5rem">
        Нет доступных статей
      </p>
    </template>
    <spinner
      :class="{
        'has-faded': this.reachedEndOfFeed,
      }"
    />
    <b-modal
      :active="previewModal.active"
      @close="() => (previewModal.active = false)"
      class="article-preview-modal is-full-screen-mobile"
      :width="previewModal.width"
      ref="articlePreviewModal"
    >
      <div class="modal-card">
        <div class="modal-card-body">
          <post
            :value="previewModal.value"
            :can-minimize="false"
            :can-click-tags="false"
          ></post>
        </div>
      </div>
    </b-modal>
    <!-- <div v-else style="margin: 200px auto"></div> -->
  </div>
</template>

<script>
import CustomTabItem from "../../TabItem.vue";
import CustomTabs from "../../Tabs.vue";
import messages from "../../mixins/messages";
import errors from "../../mixins/errors";
import toasts from "../../mixins/toasts";
import Spinner from "../../Spinner.vue";
import Post from "./app/Post.vue";

import { ResizeObserver } from "resize-observer";

const artificialLoadDelay = 500;

const request = axios.CancelToken.source();

export default Vue.extend({
  mixins: [messages, errors, toasts],
  components: {
    CustomTabItem,
    CustomTabs,
    Spinner,
    Post,
  },
  data() {
    return {
      count: 10,
      currentPage: 1,
      isLoading: false,
      loadedOnce: false,
      initialized: false,
      reachedEndOfFeed: false,
      tags: [],
      articles: [],
      previousActiveTab: null,
      activeTab: 0,
      previewModal: {
        width: null,
        value: null,
        active: false,
      },
      sections: [
        { name: "feed", icon: "rss" },
        { name: "media", icon: "newspaper-variant-outline" },
      ],
    };
  },
  async created() {
    if (this.$can("view announcements")) {
      this.sections.push({
        name: "announcements",
        icon: "bullhorn",
      });
    }

    if (this.$can("post any articles")) {
      this.sections.push({
        name: "authored",
        icon: "feather",
      });
    }

    this.sections.push({
      name: "favorites",
      icon: "heart",
    });

    this.sections.push({
      name: "my-events",
      icon: "account-multiple-check",
    });
  },
  mounted() {
    new ResizeObserver(this.onScrollableContainerResize).observe(
      this.$refs.scrollableContainer
    );
  },
  computed: {
    infiniteScrollDisabled() {
      return !this.initialized || this.reachedEndOfFeed;
    },
    sectionNames() {
      return _.map(this.sections, "name");
    },
    sectionName() {
      return this.sectionNames[this.activeTab];
    },
  },
  methods: {
    async updateState(delta) {
      if (delta.section) {
        this.changeSection(delta.section);
      }

      if (delta.tags) {
        this.tags = delta.tags;
      }

      await this.fetchInitialFeed();
    },
    changeSection(name) {
      const index = _.findIndex(this.sections, { name });

      if (index > -1) {
        this.previousActiveTab = null;
        this.activeTab = index;
      }
    },
    onTagClick(tag, append = false) {
      this.$emit("tags-change", [tag.hash]);
    },
    resetFeed(final = false) {
      this.articles = [];
      this.currentPage = 1;
      if (final) {
        this.initialized = this.reachedEndOfFeed = true;
      } else {
        this.initialized = this.reachedEndOfFeed = false;
      }
    },
    async fetchInitialFeed() {
      this.resetFeed();

      this.$emit("loading");

      if (this.request) {
        this.request.cancel("fetching initial feed");
      }

      while (
        this.articles.length == 0 ||
        this.$refs.scrollableContainer.clientHeight < window.innerHeight
      ) {
        const articles = await this.fetch();
        if (articles.length == 0) {
          this.reachedEndOfFeed = true;
          break;
        }
      }

      this.initialized = true;
      this.loadedOnce = true;

      this.$emit("loaded");
    },
    debouncedFetch: _.debounce(async function () {
      return await this.fetch();
    }, 100),
    async fetch() {
      if (this.isLoading) {
        return [];
      }

      let articles = await this.load(this.currentPage++, this.count);

      for (let article of articles) {
        this.articles.push(article);
      }

      if (articles.length == 0 && !this.isLoading) {
        this.reachedEndOfFeed = true;
      }

      return articles;
    },
    async load(page = 1, count = 1) {
      if (this.isLoading) {
        return [];
      }

      this.isLoading = true;

      this.request = axios.CancelToken.source();

      let result = [];

      try {
        let {
          data: { data },
        } = await axios.get(`//${process.env.MIX_LYNX_API_HOST}/news`, {
          cancelToken: this.request.token,
          headers: { Authorization: `Bearer ${this.$jwt}` },
          params: {
            page,
            count,
            tags: this.tags,
            author: this.sectionName == "authored" ? this.$user.id : null,
            category: this.sectionName,
          },
        });

        result = data;
      } catch (e) {
        if (axios.isCancel(e)) {
          //
        } else {
          this.report(e);
        }
      }

      this.isLoading = false;

      return result;
    },
    async onTabChange(tab) {
      if (tab == this.previousActiveTab) {
        return;
      } else {
        this.previousActiveTab = tab;
      }

      this.$emit("section-change", this.sectionName);
    },
    onScrollableContainerResize(element) {
      this.previewModal.width = _.first(element).contentRect.width;
      // Dirty hack because modal component doesn't update newWidth itself
      this.$refs.articlePreviewModal.newWidth =
        _.first(element).contentRect.width + "px";
    },
    onPostOpen(post) {
      this.previewModal.value = post;
      this.previewModal.active = true;
    },
    getSectionTabVisibility(section) {
      if (["favorites", "my-events"].includes(section.name)) {
        return section.name == this.sectionName;
      }

      return true;
    },
    getSectionTabLabelVisibility(section) {
      return this.sectionName == section.name;
    },
    async onPostVisibilityChange(isVisible, entry, article) {
      if (isVisible && article.published_at != null) {
        await axios({
          method: "post",
          headers: { Authorization: `Bearer ${this.$jwt}` },
          url: `//${process.env.MIX_LYNX_API_HOST}/news/${article.id}/views`,
        });
      }
    },
  },
});
</script>

<style scoped lang="sass">
$tagsMargin: 0.25rem

.checkbox-button-container
    display: flex
    flex-wrap: wrap
.checkbox-button
    margin: 0 $tagsMargin $tagsMargin 0
</style>

<style lang="sass">
@import "../../../../sass/app"

.article-preview-modal
    .modal-content
        width: 100% !important
    .modal-card
        width: auto !important
        +tablet
            border-radius: 3px

.category-tabs
    margin: 0
    .tab-content
        display: none
    li .icon
        margin-left: 0 !important
        &:last-child
            margin-right: 0 !important
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/views/news/Feed.vue